import { cache } from "react"
import { TTemplatesTypes } from "@/app/(site)/_templates/router"
import { i18nDefaultLocale } from "@/i18Config"
import type {
  Elements,
  IContentItem,
  IContentItemsContainer,
} from "@kontent-ai/delivery-sdk"

import type { WebSpotlightRoot } from "@/types/kontent-generated/types/web-spotlight-root.generated"
import { getDeliveryClient } from "@/lib/kontent/client"

import { env } from "../../../../env"

// ---- Resolved ApplicationTree ----
export interface ResolvedApplicationTreeItem {
  id: string
  codename: string
  urlSlug: string
  typeName: TTemplatesTypes
  name: string
  path: string
  parentId?: string
  parentCodename?: string
  parentUrlSlug?: string
  parentName?: string
  parentPath?: string
  children?: ResolvedApplicationTreeItem[]
}

export interface ResolvedApplicationTree {
  byPath: Record<string, ResolvedApplicationTreeItem>
  bySlug: Record<string, ResolvedApplicationTreeItem>
  byCodename: Record<string, ResolvedApplicationTreeItem>
}

/**
 * Processes the SDK response into a resolved application tree.
 * The SDK resolves linked items automatically, so we can traverse
 * the `linkedItems` arrays directly instead of looking up codenames
 * in `modular_content`.
 */
function _processWebSpotlightApplicationTreeSDK(
  rootItem: IContentItem,
  linkedItemsContainer: IContentItemsContainer<IContentItem>
): ResolvedApplicationTree {
  const applicationTreeByPath: Record<string, ResolvedApplicationTreeItem> = {}
  const applicationTreeBySlug: Record<string, ResolvedApplicationTreeItem> = {}
  const applicationTreeByCodename: Record<string, ResolvedApplicationTreeItem> =
    {}

  function _addItemToTree(item: ResolvedApplicationTreeItem) {
    applicationTreeByPath[item.path] = item
    applicationTreeBySlug[item.urlSlug] = item
    applicationTreeByCodename[item.codename] = item
  }

  function _processPage(
    codename: string,
    parentPath: string = "",
    parentSlug: string = ""
  ): void {
    const pageData = linkedItemsContainer[codename]

    if (!pageData) {
      console.warn(`Page missing from linkedItems: ${codename}`)
      return
    }

    const elements = pageData.elements as {
      url_slug?: Elements.TextElement
      title?: Elements.TextElement
      subpages?: { value?: (string | undefined)[] }
    }
    const urlSlug = elements.url_slug?.value
    const title = elements.title?.value

    if (!urlSlug || !title) {
      console.warn(`Page missing url_slug or title: ${codename}`)
      return
    }

    const path = parentPath ? `${parentPath}/${urlSlug}` : urlSlug

    const pageItem: ResolvedApplicationTreeItem = {
      id: pageData.system.id,
      codename,
      urlSlug,
      typeName: pageData.system.type as TTemplatesTypes,
      name: title,
      path,
      parentId: parentPath ? applicationTreeByPath[parentPath]?.id : undefined,
      parentCodename: parentPath
        ? applicationTreeByPath[parentPath]?.codename
        : undefined,
      parentUrlSlug: parentSlug,
      parentName: parentPath
        ? applicationTreeByPath[parentPath]?.name
        : undefined,
      parentPath,
      children: [],
    }

    _addItemToTree(pageItem)

    // Process subpages — the SDK stores subpage codenames in elements.subpages.value
    const subpageCodenames =
      elements.subpages?.value?.filter(
        (c: string | undefined): c is string => !!c
      ) || []

    subpageCodenames.forEach((subCodename: string) =>
      _processPage(subCodename, path, urlSlug)
    )
  }

  // Process root subpages
  const rootElements = rootItem.elements as {
    subpages?: { value?: (string | undefined)[] }
  }
  const rootSubpages = rootElements.subpages?.value?.filter(
    (c: string | undefined): c is string => !!c
  )

  if (rootSubpages) {
    rootSubpages.forEach((codename: string) => _processPage(codename))
  }

  return {
    byPath: applicationTreeByPath,
    bySlug: applicationTreeBySlug,
    byCodename: applicationTreeByCodename,
  }
}

async function _fetchWebSpotlightRoot(previewMode: boolean = false) {
  const client = getDeliveryClient(previewMode)

  return await client
    .item<WebSpotlightRoot>(env.KONTENT_APP_CODENAME)
    .languageParameter(i18nDefaultLocale)
    // Excludes fields only needed by the footer (site_configuration's
    // address/social/copyright/lost-or-stolen-card fields, footer_menu) and
    // page-only fields (hero_section, seo, image, body, has_anchor,
    // variables). Deliberately does NOT exclude "content": that codename is
    // also used by HighlightedContent items nested in the footer's
    // prefooter, and Kontent's exclude filter applies by field name at every
    // nesting level, not just the root — excluding it here would silently
    // strip that nested content too. Because "content" stays in, the
    // footer's own query (getFooterMenu.ts) can't be merged into this one
    // without pulling in the root's full page-builder tree and exceeding
    // Next.js's 2MB Data Cache limit (verified empirically: an unfiltered
    // depth-5 fetch of this item is ~2.8MB and Next.js refuses to cache it,
    // turning every page view into an uncached live request). This filtered
    // version measured ~578KB / X-Request-Charge 276, comfortably under both
    // the cache limit and Kontent's own suggested 1000-charge threshold.
    .excludeElementsParameter([
      "hero_section",
      "content",
      "has_anchor",
      "footer_menu",
      "variables",
      "copyright",
      "seo",
      "lost_or_stolen_credit_card",
      "lost_or_stolen_debit_card",
      "address_line_2",
      "address_line_1",
      "tva",
      "clearing",
      "bic_swift",
      "linkedin_link",
      "youtube_link",
      "instagram_link",
      "facebook_link",
      "image",
      "body",
    ])
    .depthParameter(5)
    .toPromise()
}

/**
 * Fetches the WebSpotlight root item ("expat") once, shared by
 * getWebSpotlightApplicationTree, getHeaderMenu and getGlobalAlert — the
 * three consumers whose needs don't collide with the exclusions above.
 * getFooterMenu keeps its own separate fetch (see the exclusion comment).
 * Because this is wrapped in React `cache()`, the three call sites below
 * collapse into a single Delivery API request per render instead of three.
 */
export const getKontentSiteRoot = cache(_fetchWebSpotlightRoot)

async function _fetchWebSpotlightApplicationTree(
  previewMode: boolean = false
): Promise<ResolvedApplicationTree> {
  const response = await getKontentSiteRoot(previewMode)

  return _processWebSpotlightApplicationTreeSDK(
    response.data.item,
    response.data.linkedItems as IContentItemsContainer<IContentItem>
  )
}

export const getWebSpotlightApplicationTree = cache(
  _fetchWebSpotlightApplicationTree
)
